import api from "@/lib/axiosConfig";
import { Exhibition } from "@/lib/types";
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { RelativePosition } from "../dtos";

interface ExhibitionDTO {
  where: any;
  order: any;
  take: number;
  skip: number;
}

export const baseRoute = "back/api/exhibitions";

async function getExhibitions({
  where,
  order,
  take,
  skip,
}: ExhibitionDTO): Promise<Exhibition[]> {
  const { data } = await api.post(
    `back/api/exhibitions?take=${take || 10}&skip=${skip || 0}`,
    { where, orderBy: order },
  );
  return data;
}

export const useGetAllExhibitions = ({
  where,
  order,
  take,
  skip,
}: ExhibitionDTO) => {
  return useQuery({
    queryKey: ["exhibitions", where, order, take, skip],
    queryFn: () => getExhibitions({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

export async function getCountByStatus(where?: object) {
  const { data } = await api.post(`back/api/exhibitions/countByStatus`, {
    where,
  });
  return data;
}

export const useCountByStatus = () => {
  return useQuery({
    queryKey: ["countByStatus", "exhibition"],
    queryFn: () => getCountByStatus(),
  });
};

export const getRelativePosition = async (
  id: string,
  order: object,
  where: object,
) => {
  const { data } = await api.post<RelativePosition>(
    `${baseRoute}/${id}/position`,
    {
      order,
      where,
    },
  );
  return data;
};

async function getExhibition(id: string) {
  const { data } = await api.get<Exhibition>(`back/api/exhibitions/${id}`);
  return data;
}

export function useGetExhibition(id?: string) {
  return useQuery({
    queryKey: ["exhibition", id],
    queryFn: () => (id ? getExhibition(id) : undefined),
  });
}

async function updateExhibition(exhibition: Exhibition) {
  const {
    instanceId: _iid,
    expositionsDescriptionExpositionPersonnelleDeId: _epdid,
    updatedById: _ubid,
    createdById: _cbid,
    ...rest
  } = exhibition;
  return await api.patch(`back/api/exhibitions/${exhibition.id}`, {
    data: rest,
  });
}

export function useUpdateExhibition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateExhibition,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["exhibition", v.id] });
      queryClient.invalidateQueries({ queryKey: ["exhibitions"] });
    },
  });
}

type CreateExhibitionParams = {
  expositionsDescriptionNumeroExpo: string;
  expositionsDescriptionTitre: string;
};
type CreateExhibitionResponse = Pick<Exhibition, "id">;

const createExhibition = async ({
  expositionsDescriptionNumeroExpo,
  expositionsDescriptionTitre,
}: CreateExhibitionParams) => {
  return await api
    .post<CreateExhibitionResponse>("back/api/exhibitions/create", {
      expositionsDescriptionNumeroExpo,
      expositionsDescriptionTitre,
    })
    .then((res) => res.data);
};

export const useCreateExhibition = (
  options?: UseMutationOptions<
    CreateExhibitionResponse,
    Error,
    CreateExhibitionParams
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createExhibition,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["exhibitions"] });
      queryClient.invalidateQueries({ queryKey: ["exhibition"] });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "exhibition"],
      });
      queryClient.invalidateQueries({ queryKey: ["instance", "count-data"] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};

export const looselyRelatedAuthorSchema = z.object({ id: z.string() }).and(
  z.union([
    z.object({
      fallbackLabel: z.string(),
    }),
    z.object({
      author: z.object({
        id: z.string(),
        artistesAuteursDescriptionNom: z.string(),
        artistesAuteursDescriptionPrenom: z.string(),
      }),
    }),
  ]),
);

export type LooselyRelatedAuthor = z.infer<typeof looselyRelatedAuthorSchema>;

async function getLooselyRelatedAuthors(exhibitionId: string) {
  const { data } = await api.get<LooselyRelatedAuthor[]>(
    `${baseRoute}/${exhibitionId}/loosely-related/authors`,
  );
  return data;
}

export const useGetLooselyRelatedAuthors = (exhibitionId: string) => {
  return useQuery({
    queryKey: ["exhibition", exhibitionId, "looselyRelatedAuthors"],
    queryFn: () => getLooselyRelatedAuthors(exhibitionId),
  });
};

export const createOrUpdateLooselyRelatedAuthorSchema = z.union([
  z.object({ fallbackLabel: z.string().min(1) }),
  z.object({ authorId: z.string().uuid() }),
]);

export type CreateOrUpdateLooselyRelatedAuthor = z.infer<
  typeof createOrUpdateLooselyRelatedAuthorSchema
>;

export function looselyRealatedAuthorToCreateOrUpdate(
  author: LooselyRelatedAuthor,
): CreateOrUpdateLooselyRelatedAuthor {
  if ("fallbackLabel" in author) {
    return {
      fallbackLabel: author.fallbackLabel,
    };
  }
  return { authorId: author.author.id };
}

async function updateLooselyRelatedAuthors({
  looselyRelatedAuthorId,
  data,
}: {
  exhibitionId: string;
  looselyRelatedAuthorId: string;
  data: CreateOrUpdateLooselyRelatedAuthor;
}) {
  await api.patch(
    `${baseRoute}/loosely-related/authors/${looselyRelatedAuthorId}`,
    data,
  );
}

export const useUpdateLooselyRelatedAuthor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLooselyRelatedAuthors,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({
        queryKey: ["exhibition", v.exhibitionId, "looselyRelatedAuthors"],
      });
    },
  });
};

async function deleteLooselyRelatedAuthor({
  looselyRelatedAuthorId,
}: {
  exhibitionId: string;
  looselyRelatedAuthorId: string;
}) {
  await api.delete(
    `${baseRoute}/loosely-related/authors/${looselyRelatedAuthorId}`,
  );
}

export const useDeleteLooselyRelatedAuthor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteLooselyRelatedAuthor,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({
        queryKey: ["exhibition", v.exhibitionId, "looselyRelatedAuthors"],
      });
    },
  });
};

async function createLooselyRelatedAuthors({
  exhibitionId,
  data,
}: {
  exhibitionId: string;
  data: CreateOrUpdateLooselyRelatedAuthor;
}) {
  await api.post(`${baseRoute}/${exhibitionId}/loosely-related/authors`, data);
}

export const useCreateLooselyRelatedAuthor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createLooselyRelatedAuthors,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({
        queryKey: ["exhibition", v.exhibitionId, "looselyRelatedAuthors"],
      });
    },
  });
};

export const getAllIds = async (where?: object) => {
  const { data } = await api.post<{ id: string }[]>(`${baseRoute}/ids`, {
    where,
  });
  return data;
};
